"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";

import { useLanguage } from "../i18n/LanguageProvider";

const JUMLAH_FOTO = 44;
const FOLDER = "/images/gallery";
const EKSTENSI = "jpg";

/* Dikunci ke nama berkas, bukan ke posisi dalam array. Versi lama
   memakai array + komentar "// 19.jpg", dan satu nomor yang terlewat
   menggeser semua keterangan di bawahnya tanpa error apa pun. Di sini
   kuncinya adalah nama berkasnya sendiri, jadi urutan tidak berpengaruh
   dan mencari "19.jpg" di editor langsung menemukan barisnya. */
const KETERANGAN = {
  "1.jpg": "",
  "2.jpg": "",
  "3.jpg": "",
  "4.jpg": "",
  "5.jpg": "",
  "6.jpg": "",
  "7.jpg": "",
  "8.jpg": "",
  "9.jpg": "",
  "10.jpg": "",
  "11.jpg": "",
  "12.jpg": "",
  "13.jpg": "",
  "14.jpg": "",
  "15.jpg": "",
  "16.jpg": "",
  "17.jpg": "",
  "18.jpg": "",
  "19.jpg": "",
  "20.jpg": "",
  "21.jpg": "",
  "22.jpg": "",
  "23.jpg": "",
  "24.jpg": "",
  "25.jpg": "",
  "26.jpg": "",
  "27.jpg": "",
  "28.jpg": "",
  "29.jpg": "",
  "30.jpg": "",
  "31.jpg": "",
  "32.jpg": "",
  "34.jpg": "",
  "35.jpg": "",
  "36.jpg": "",
  "37.jpg": "",
  "38.jpg": "",
  "39.jpg": "",
  "40.jpg": "",
  "41.jpg": "",
  "42.jpg": "",
  "43.jpg": "",
  "44.jpg": "",
};

const buatFoto = (t) =>
  Array.from({ length: JUMLAH_FOTO }, (_, i) => {
    const berkas = `${i + 1}.${EKSTENSI}`;
    const teks = KETERANGAN[berkas] || "";

    return {
      src: `${FOLDER}/${berkas}`,
      teks,
      alt: teks || t("galeri.altBawaan", { nomor: i + 1, total: JUMLAH_FOTO }),
    };
  });

export default function GalleryGrid() {
  const { t } = useLanguage();
  const FOTO = useMemo(() => buatFoto(t), [t]);

  const [aktif, setAktif] = useState(-1);
  const tombolRefs = useRef([]);
  const pemicu = useRef(-1);
  const dialogRef = useRef(null);

  const terbuka = aktif >= 0;

  const buka = (i) => {
    pemicu.current = i;
    setAktif(i);
  };

  /* Fokus dikembalikan ke ubin yang tadi diklik. Tanpa ini, menutup
     lightbox melempar fokus ke awal dokumen dan pengguna keyboard
     harus menekan Tab puluhan kali untuk kembali ke posisinya. */
  const tutup = useCallback(() => {
    setAktif(-1);
    const el = tombolRefs.current[pemicu.current];
    if (el) el.focus();
  }, []);

  const geser = useCallback((arah) => {
    setAktif((i) => (i + arah + JUMLAH_FOTO) % JUMLAH_FOTO);
  }, []);

  useEffect(() => {
    if (!terbuka) return;

    /* Fokus dipindahkan ke dalam dialog saat dibuka. Tanpa ini fokus
       tetap tertinggal di ubin yang ada di belakang lapisan gelap. */
    dialogRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") return tutup();
      if (e.key === "ArrowLeft") return geser(-1);
      if (e.key === "ArrowRight") return geser(1);
      if (e.key !== "Tab") return;

      /* Tab dikurung di dalam dialog. Kalau tidak, penekanan Tab
         berikutnya masuk ke grid di belakangnya yang secara visual
         tertutup - fokus jadi tidak kelihatan di mana pun. */
      const fokusable = dialogRef.current?.querySelectorAll("button");
      if (!fokusable?.length) return;

      const pertama = fokusable[0];
      const terakhir = fokusable[fokusable.length - 1];

      if (e.shiftKey && document.activeElement === pertama) {
        e.preventDefault();
        terakhir.focus();
      } else if (!e.shiftKey && document.activeElement === terakhir) {
        e.preventDefault();
        pertama.focus();
      }
    };

    /* Scroll halaman dikunci. Nilai aslinya disimpan dan dikembalikan,
       bukan dipaksa jadi "auto" - kalau ada style lain yang menyetel
       overflow body, memaksanya akan merusak itu. */
    const asli = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = asli;
      document.removeEventListener("keydown", onKey);
    };
  }, [terbuka, tutup, geser]);

  return (
    <>
      {/* ---------- GRID ----------
         Foto pertama menempati 2x2 mulai md. Grid yang seluruh
         ubinnya seukuran terbaca seperti daftar berkas; satu ubin
         besar memberi titik masuk bagi mata dan membuat halaman
         terasa disusun, bukan sekadar diunggah.

         auto-rows-fr + aspect pada ubin: baris ikut tinggi ubin, jadi
         ubin besar benar-benar 2 baris penuh tanpa celah. */}
      <ul className="grid auto-rows-fr grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4 lg:gap-4">
        {FOTO.map((foto, i) => (
          <li key={foto.src} className={i === 0 ? "md:col-span-2 md:row-span-2" : ""}>
            <button
              type="button"
              ref={(el) => {
                tombolRefs.current[i] = el;
              }}
              onClick={() => buka(i)}
              aria-label={t("galeri.perbesar", { teks: foto.alt })}
              className="group relative block h-full w-full overflow-hidden rounded-lg bg-[var(--c-tint)] ring-1 ring-[var(--c-line)] transition-shadow duration-300 hover:shadow-[0_1px_2px_rgba(47,4,37,0.12),0_16px_32px_-12px_rgba(47,4,37,0.25)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] sm:rounded-xl"
            >
              {/* aspect di sini, bukan di <li>, supaya ubin besar bisa
                 mengisi dua baris penuh tanpa memaksa rasionya. */}
              <div className={`relative w-full ${i === 0 ? "aspect-[4/3] md:aspect-auto md:h-full" : "aspect-[4/3]"}`}>
                <Image
                  src={foto.src}
                  alt={foto.alt}
                  fill
                  sizes={i === 0 ? "(min-width: 1024px) 50vw, (min-width: 640px) 66vw, 50vw" : "(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw"}
                  className="object-cover object-center transition-transform duration-500 ease-out group-hover:scale-105 motion-reduce:transform-none"
                />
              </div>

              {/* Lapisan gelap tipis saat kursor mendekat + ikon
                 perbesar. Ini yang memberi tahu bahwa ubinnya bisa
                 diklik - tanpa isyarat, grid foto terbaca sebagai
                 gambar biasa. */}
              <span aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[var(--c-panel-deep)]/0 transition-colors duration-300 group-hover:bg-[var(--c-panel-deep)]/35 motion-reduce:transition-none">
                <span className="flex h-10 w-10 scale-90 items-center justify-center rounded-full bg-white/95 opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100 motion-reduce:transition-none">
                  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-[var(--c-brand)]">
                    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                    <path d="M11 8v6M8 11h6M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
              </span>

              {/* Garis kuning tipis di dasar ubin - aksen merek yang
                 sama dipakai kartu Center of Excellence. */}
              <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1 scale-x-0 bg-[var(--c-red)] transition-transform duration-300 ease-out group-hover:scale-x-100 motion-reduce:transition-none" />
            </button>
          </li>
        ))}
      </ul>

      {/* ---------- LIGHTBOX ---------- */}
      {terbuka && (
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={t("galeri.fotoKe", { nomor: aktif + 1, total: JUMLAH_FOTO })}
          tabIndex={-1}
          onClick={tutup}
          className="fixed inset-0 z-[100] flex flex-col bg-[var(--c-panel-deep)]/95 p-3 backdrop-blur-sm focus:outline-none sm:p-6"
        >
          {/* BARIS ATAS: penghitung + tombol tutup */}
          <div className="flex shrink-0 items-center justify-between gap-4 pb-3 sm:pb-4">
            <p className="text-sm font-semibold tabular-nums text-white/80">
              {aktif + 1} / {JUMLAH_FOTO}
            </p>

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); tutup(); }}
              aria-label={t("galeri.tutup")}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/25 transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-5 w-5">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* FOTO. stopPropagation supaya mengklik fotonya sendiri
             tidak ikut menutup - hanya latar di sekitarnya. */}
          <div className="relative min-h-0 flex-1" onClick={(e) => e.stopPropagation()}>
            <Image
              key={FOTO[aktif].src}
              src={FOTO[aktif].src}
              alt={FOTO[aktif].alt}
              fill
              sizes="100vw"
              priority
              className="object-contain object-center"
            />
          </div>

          {/* BARIS BAWAH: panah + keterangan */}
          <div className="flex shrink-0 items-center justify-between gap-4 pt-3 sm:pt-4" onClick={(e) => e.stopPropagation()}>
            <TombolNav arah="kiri" onClick={() => geser(-1)} t={t} />

            {FOTO[aktif].teks ? (
              <p className="min-w-0 flex-1 text-center text-[13px] leading-snug text-white/85 sm:text-sm">
                {FOTO[aktif].teks}
              </p>
            ) : (
              <span className="flex-1" />
            )}

            <TombolNav arah="kanan" onClick={() => geser(1)} t={t} />
          </div>
        </div>
      )}
    </>
  );
}

function TombolNav({ arah, onClick, t }) {
  const kiri = arah === "kiri";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={kiri ? t("galeri.fotoSebelumnya") : t("galeri.fotoBerikutnya")}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/25 transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:h-12 sm:w-12"
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-5 w-5">
        <path d={kiri ? "M19 12H5m0 0 6-6m-6 6 6 6" : "M5 12h14m0 0-6-6m6 6-6 6"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}
